import { all, call, put, takeEvery, take } from 'redux-saga/effects'

import { asApiError, handleError } from 'api/api-utils'
import reportApi from 'api/ReportApi'

import {
  getReportContextDataError,
  getReportContextDataSuccess,
  getReportContextDataProgressSuccess,
  invalidateReportContextDataCacheError,
  invalidateReportContextDataCacheSuccess,
  reportContextDataWaitingForCalculation,
} from './actions'
import {
  GET_REPORT_CONTEXT_DATA,
  INVALIDATE_REPORT_CONTEXT_DATA_CACHE,
} from './constants'
import { eventChannel, END } from 'redux-saga'
import {
  readUntilCalculationFinished,
  recordCalculationRefusal,
} from 'utils/actualsCalculationWait'

// Individual exports for testing
let payloadCache = []
export function* getReportContextData(action) {
  const { companyCode, enableCaching, reportId } = action
  // State is keyed by company and report (the reducer and the widgets both key off
  // [companyCode, reportId]), so two companies reading the same report are independent reads.
  //
  // What the caller wants from the cache is part of the key as well. A widget asks for an uncached
  // re-read when a drill-down turns out to point at rows a recalculation has deleted, and that ask
  // must not be dropped as a duplicate of an ordinary cached read that happens to be in flight -
  // which, with the calculation wait, can now be in flight for minutes. Two widgets mounting on the
  // same report and company still make one request between them, which is what this is for.
  const cacheKey = [
    companyCode,
    reportId,
    enableCaching ? 'cached' : 'fresh',
  ].join(':')

  if (payloadCache.includes(cacheKey)) return
  try {
    payloadCache = [...payloadCache, cacheKey]

    yield call(
      readUntilCalculationFinished,
      (outcome) => readReportContextDataOnce(action, outcome),
      {
        waiting: (companies) =>
          reportContextDataWaitingForCalculation({
            companyCode,
            reportId,
            companies,
          }),
        giveUp: (frame) =>
          handleError(asApiError(frame), getReportContextDataError, {
            reportId,
            companyCode,
          }),
      }
    )
  } catch (error) {
    yield put(
      handleError(error, getReportContextDataError, { reportId, companyCode })
    )
  } finally {
    payloadCache = payloadCache.filter((key) => key !== cacheKey)
  }
}

/**
 * Reads the report once, reporting back by writing into `outcome`:
 * `outcome.calculationInProgress` is set to `{ error, companies }` when the API refused because an
 * actuals calculation is writing the figures of one of the report's companies, which is the
 * caller's cue to ask again, and left unset when the read finished - with data, or with an error
 * that has already been dispatched.
 *
 * It cannot be a return value. The channel is closed with END once the read is over, and redux-saga
 * answers END by *terminating* this generator at that `take` rather than resuming it: nothing after
 * the loop runs, and `call` resolves to undefined.
 */
export function* readReportContextDataOnce(action, outcome) {
  const { companyCode, customerCode, enableCaching, reportId } = action

  const channel = eventChannel((eventEmmit) => {
    let reportRows = []
    const onMessage = (event) => {
      try {
        const parsed = JSON.parse(event.data)

        if (event.event === 'data-batch') {
          // Backend double-serializes: parse the inner JSON string containing the batch array
          const batchItems = JSON.parse(parsed.items)
          reportRows.push(...batchItems) // Spread to add all items from batch
          return
        }

        if (event.event === 'data-complete') {
          eventEmmit(
            getReportContextDataSuccess({
              report: reportRows,
              reportId,
              customerCode,
              companyCode,
            })
          )
          reportRows = []
          eventEmmit(END)
          return
        }
      } catch (error) {
        eventEmmit(
          handleError(error, getReportContextDataError, {
            reportId,
            companyCode,
          })
        )
        eventEmmit(END)
      }
    }

    const onProgress = (event) => {
      eventEmmit(
        getReportContextDataProgressSuccess({
          progress: event.data,
          ...action,
        })
      )
    }

    const onError = (event) => {
      const { refused, parsedError } = recordCalculationRefusal(
        event,
        outcome,
        eventEmmit
      )
      if (refused) return

      // Wrapped the way every other error path arrives - ErrorComponent reads props.data, and
      // handing over the parsed body on its own renders a bare "Something went wrong" with
      // neither the server's message nor its code.
      eventEmmit(
        handleError(asApiError(parsedError), getReportContextDataError, {
          reportId,
          companyCode,
        })
      )
      eventEmmit(END)
    }

    reportApi.getReport({
      companyCode,
      customerCode,
      enableCaching,
      reportId,
      onMessage,
      onProgress,
      onError,
    })

    return () => {}
  })

  // No exit of its own: END terminates this generator where it waits, so the loop is left by
  // termination and never by falling out of it.
  while (true) {
    const event = yield take(channel)
    yield put(event)
  }
}

export function* invalidateReportContextDataCache(action) {
  const { companyCode, reportId } = action
  try {
    yield call(reportApi.invalidateReport, { reportId })
    yield put(
      invalidateReportContextDataCacheSuccess({ companyCode, reportId })
    )
  } catch (error) {
    yield put(
      handleError(error, invalidateReportContextDataCacheError, { companyCode })
    )
  }
}

export function* reportContextDatasSaga() {
  yield all([
    takeEvery(GET_REPORT_CONTEXT_DATA, getReportContextData),
    takeEvery(
      INVALIDATE_REPORT_CONTEXT_DATA_CACHE,
      invalidateReportContextDataCache
    ),
  ])
}

export default reportContextDatasSaga
